import { stringIncludesInsensitiveAccents } from '../../utils/normalizeForSearch';

/**
 * Count total columns from options
 * @param {Array<ApiManagerTableFiltersSearchOptions> | undefined | null} options
 * @returns {number}
 */
export function tableColumnsCount(options) {
  if (!options || !options.length) return 0;
  return options.length;
}

/**
 * Return list of all column ids from options
 * @param {Array<ApiManagerTableFiltersSearchOptions> | undefined | null} options
 * @returns {string[]}
 */
export function getAllColumnIds(options) {
  if (!options || !options.length) return [];
  return options.map((option) => option.id);
}

/**
 * Check if all columns are selected
 * @param {Record<string, boolean> | undefined | null} selectedColumns - Object with IDs as keys (internal format)
 * @param {number} totalColumns - Total number of columns
 * @returns {boolean} True if all columns are selected
 */
export function areAllColumnsSelected(selectedColumns, totalColumns) {
  if (!totalColumns) return false;
  const selectedCount = selectedColumns ? Object.keys(selectedColumns).length : 0;
  return selectedCount === totalColumns;
}

/**
 * True when some but not all columns are selected (select-all checkbox indeterminate).
 * @param {Record<string, boolean> | undefined | null} selectedColumns
 * @param {number} totalColumns
 * @returns {boolean}
 */
export function isAllColumnsSelectionIndeterminate(selectedColumns, totalColumns) {
  if (!totalColumns) return false;
  const selectedCount = selectedColumns ? Object.keys(selectedColumns).length : 0;
  return selectedCount > 0 && selectedCount < totalColumns;
}

/**
 * True when some but not all columns in the group ids are selected.
 * @param {Record<string, boolean> | undefined | null} selectedColumns
 * @param {string[]} ids
 * @returns {boolean}
 */
export function isColumnsGroupSelectionIndeterminate(selectedColumns, ids) {
  if (!ids?.length) return false;
  const selected = selectedColumns || {};
  const anySelected = ids.some((id) => selected[id]);
  const allSelected = ids.every((id) => selected[id]);
  return anySelected && !allSelected;
}

/**
 * Filter search options by search
 * - Case-insensitive and accent-insensitive
 * - Matches on option.lib and option.optGroup
 * @param {Array<ApiManagerTableFiltersSearchOptions>} options
 * @param {string} search
 * @returns {Array<ApiManagerTableFiltersSearchOptions>}
 */
export function searchInSearchOptions(options, search) {
  if (!options || !options.length) return [];
  const searchTrimmed = (search || '').trim();
  if (!searchTrimmed) return options;

  return options.filter((option) => {
    const libMatch = stringIncludesInsensitiveAccents(option.lib, searchTrimmed);
    const optGroupMatch = stringIncludesInsensitiveAccents(option.optGroup, searchTrimmed);
    return libMatch || optGroupMatch;
  });
}

/**
 * Validate a plain object map of booleans
 * @param {any} value
 * @returns {value is Record<string, boolean>}
 */
export function isBooleanMap(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  return Object.values(value).every((v) => typeof v === 'boolean');
}

/**
 * Convert array of IDs to object format for internal state
 * Used to convert API format (Array<string>) to internal format (Record<string, boolean>)
 * @param {string[]} ids - Array of selected column IDs (from API search.selectedOptions)
 * @returns {Record<string, boolean>} Object with IDs as keys and true as values
 */
export function convertArrayToObject(ids) {
  if (!ids || !Array.isArray(ids)) return {};
  return ids.reduce((acc, id) => {
    acc[id] = true;
    return acc;
  }, /** @type {Record<string, boolean>} */ ({}));
}

/**
 * Convert object format to array of IDs for API calls
 * Used to convert internal format (Record<string, boolean>) to API format (Array<string>)
 * @param {Record<string, boolean>} obj - Object with IDs as keys (internal state)
 * @returns {string[]} Array of selected column IDs (for API search.selectedOptions)
 */
export function convertObjectToArray(obj) {
  if (!obj || typeof obj !== 'object') return [];
  return Object.keys(obj).filter((id) => obj[id] === true);
}
